import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  terminate,
  clearIndexedDbPersistence,
  type Firestore,
} from 'firebase/firestore';
import { getDatabase, connectDatabaseEmulator, type Database } from 'firebase/database';
import type { FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { readEnv, USE_EMULATORS, type FirebaseEnv } from './env';

/**
 * Firebase 클라이언트 싱글턴 (PLAN 2장, 부록 A-6).
 *
 * 주의: Firestore은 오프라인 영속 캐시로 초기화해야 한다.
 * `getFirestore()`는 이미 초기화된 인스턴스를 반환하므로
 * `persistentLocalCache`를 적용할 수 없다 — 반드시 `initializeFirestore`를 쓴다.
 */
let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;
let rtdb: Database | undefined;
let storage: FirebaseStorage | undefined;
let functions: Functions | undefined;

function getFirebaseApp(env: FirebaseEnv): FirebaseApp {
  if (app) return app;
  app =
    getApps()[0] ??
    initializeApp({
      apiKey: env.apiKey,
      authDomain: env.authDomain,
      projectId: env.projectId,
      storageBucket: env.storageBucket,
      messagingSenderId: env.messagingSenderId,
      appId: env.appId,
      databaseURL: env.databaseURL,
    });
  return app;
}

/** 에뮬레이터 연결 여부 — terminate 후 새 인스턴스를 만들면 다시 붙여야 한다 */
let firestoreEmulatorConnected = false;

/**
 * 영속 캐시(IndexedDB)를 쓰는 Firestore 인스턴스 생성.
 * `getFirestore()`는 이미 초기화된 인스턴스를 반환해 설정을 못 바꾸므로
 * 반드시 `initializeFirestore`를 쓴다 — 잠금 후 재생성 경로도 이 함수를 거친다.
 */
function createFirestore(firebaseApp: FirebaseApp): Firestore {
  const instance = initializeFirestore(firebaseApp, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
  if (USE_EMULATORS && !firestoreEmulatorConnected) {
    connectFirestoreEmulator(instance, '127.0.0.1', 8080);
    firestoreEmulatorConnected = true;
  }
  return instance;
}

/** 앱 초기화 — main.tsx에서 1회 호출 */
export function initFirebase(env?: FirebaseEnv) {
  // import.meta 전체를 넘기면 Vite가 정적 치환을 못 해 env 값이 전부 undefined가 된다.
  // 반드시 import.meta.env 를 넘겨야 각 VITE_ 변수가 문자열로 치환된다.
  const config = env ?? readEnv(import.meta.env as unknown as Record<string, string>);
  const firebaseApp = getFirebaseApp(config);

  auth ??= getAuth(firebaseApp);
  db ??= createFirestore(firebaseApp);
  rtdb ??= config.databaseURL ? getDatabase(firebaseApp) : undefined;
  functions ??= getFunctions(firebaseApp, 'asia-northeast3');

  if (USE_EMULATORS) {
    void setPersistence(auth, browserLocalPersistence);
    // 중복 연결 방지를 위해 SDK 내부 상태로 가드
    if (!(auth as unknown as { emulatorConnected?: boolean }).emulatorConnected) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      (auth as unknown as { emulatorConnected?: boolean }).emulatorConnected = true;
    }
    if (rtdb) connectDatabaseEmulator(rtdb, '127.0.0.1', 9000);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }

  return { app: firebaseApp, auth, db, rtdb, functions };
}

export function getAuthClient(): Auth {
  if (!auth) throw new Error('initFirebase()를 먼저 호출하세요.');
  return auth;
}

/**
 * FirebaseApp 인스턴스 접근자 — FCM getToken처럼 서비스별 팩토리에 앱을 직접 넘겨야
 * 하는 API가 쓴다. initFirebase 전에 호출하면 null을 돌려준다(예외 대신) — 푸시 등록처럼
 * 실패해도 앱 동작에 지장 없는 부가 기능이 예외로 전역 초기화를 깨면 안 된다.
 */
export function getFirebaseAppClient(): FirebaseApp | null {
  return app ?? null;
}
export function getDb(): Firestore {
  if (!app) throw new Error('initFirebase()를 먼저 호출하세요.');
  // purgeLocalFirestoreCache()로 비운 뒤에는 여기서 빈 캐시의 새 인스턴스를 만든다
  db ??= createFirestore(app);
  return db;
}

/**
 * 로컬 Firestore 흔적 제거 (PLAN 5-12장 — PIN 잠금 시 캐시 정리).
 *
 * 1) `terminate()`가 살아 있는 모든 `onSnapshot` 리스너를 끊는다.
 * 2) 그 뒤에야 IndexedDB 영속 캐시를 삭제할 수 있다 — SDK가
 *    "초기화 전이거나 terminate 이후"에만 허용한다.
 * 3) 다음 `getDb()`는 빈 캐시로 새 인스턴스를 만들어 네트워크에서 다시 읽는다.
 *
 * ⚠️ multi-tab 영속 캐시를 쓰므로 다른 탭이 열려 있으면 그쪽 캐시도 함께 사라진다.
 * 모바일 단일 탭 사용을 전제로 한 트레이드오프다.
 */
export async function purgeLocalFirestoreCache(): Promise<void> {
  const instance = db;
  // 동기적으로 먼저 비운다 — StrictMode/중복 호출은 여기서 즉시 빠져나간다
  db = undefined;
  firestoreEmulatorConnected = false;
  if (!instance) return;

  try {
    await terminate(instance);
    await clearIndexedDbPersistence(instance);
  } catch (error) {
    console.error('[talk2nIcK] Firestore 로컬 캐시 삭제 실패:', error);
  }
}

export function getRtdb(): Database {
  if (!rtdb) throw new Error('initFirebase()를 먼저 호출하세요. (databaseURL 필요)');
  return rtdb;
}

/**
 * Realtime Database 사용 가능 여부.
 * presence/typing은 부가 기능이라 databaseURL이 없으면 조용히 꺼야 한다 —
 * 잠금 해제나 대화방 진입이 이 때문에 실패하면 안 된다.
 */
export function isRtdbAvailable(): boolean {
  return rtdb !== undefined;
}

export async function getStorageClient(): Promise<FirebaseStorage> {
  if (storage) return storage;
  if (!app) throw new Error('initFirebase()를 먼저 호출하세요.');
  const { getStorage, connectStorageEmulator } = await import('firebase/storage');
  storage = getStorage(app);
  if (USE_EMULATORS) {
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  }
  return storage;
}

export function getFunctionsClient(): Functions {
  if (!functions) throw new Error('initFirebase()를 먼저 호출하세요.');
  return functions;
}
